import Dexie, { Table } from 'dexie';
import { Injectable } from '@angular/core';
import { DBVersion, TableDefs } from '../../db.consts';
import { WebWorkerService } from '@services/web-worker/web-worker.service';
import { awaitHandler } from '@utils/awaitHandler';
import { NGXLogger } from 'ngx-logger';

const dbPrefix = `corvex-`;

@Injectable({
  providedIn: 'root'
})
export class LocalDBService {
  // updateTimes!: Table<IUpdateTime, string>;
  // observations!: Table<IObservationItem, number>;
  // disclaimers!: Table<IDisclaimer, number>;
  // properties!: Table<IProperty, number>;
  // responses!: Table<IResponse, string>;

  public db: Dexie;

  constructor(
    private worker: WebWorkerService,
    private logger: NGXLogger
  ) {}

  public initialize(subscriberID: number, userid: number | string): Promise<void> {
    return new Promise(async (resolve, reject) => {
      if (this.db) {
        await this.db.close();
        this.db = null;
      }

      const dbName = `${dbPrefix}-${subscriberID}-${userid}`;

      // Clean up stale IndexedDB databases from previous sessions (CVX-5918)
      await this.cleanupStaleDatabases(dbName);
      // try to initialize the web worker - do it first in case it was connected to the old DB
      const [res, err] = await awaitHandler(this.worker.initDB(dbName));
      if (err) {
        this.logger.error(`failed to initialize worker db connection: ${err}`);
      }

      this.logger.debug(`opening connection to database ${dbName}`);
      this.db = new Dexie(dbName);

      this.db.version(DBVersion).stores(TableDefs);
      // block until we KNOW the datayase is ready
      this.db.on('ready', () => {
        this.logger.debug('database reports ready');
        resolve();
      });
      this.db.open();
    });
  }


  /**
   * Remove IndexedDB databases from other sessions to free storage quota.
   * Keeps only the database for the current subscriber+user.
   */
  private async cleanupStaleDatabases(currentDbName: string): Promise<void> {
    try {
      if (!indexedDB.databases) {
        return; // API not available (Firefox <126)
      }
      const allDbs = await indexedDB.databases();

      // CVX-5918: scope cleanup to the SAME user only, so we do not destroy
      // other users' offline data on shared devices (kiosks, shared tablets).
      // dbName pattern is `${dbPrefix}-${subscriberID}-${userid}`, so the
      // trailing "-${userid}" segment identifies the same user across
      // different subscriber contexts.
      const userIdSegment = '-' + currentDbName.split('-').pop();

      const staleNames = allDbs
        .map(db => db.name)
        .filter(name =>
          !!name &&
          name.startsWith(dbPrefix) &&
          name !== currentDbName &&
          name.endsWith(userIdSegment)
        );

      let deletedCount = 0;
      const blocked: string[] = [];
      const errored: string[] = [];
      for (const name of staleNames) {
        this.logger.debug(`removing stale database for current user: ${name}`);
        const outcome = await new Promise<'deleted' | 'error' | 'blocked'>((resolve) => {
          const req = indexedDB.deleteDatabase(name);
          req.onsuccess = () => resolve('deleted');
          req.onerror = () => resolve('error');
          req.onblocked = () => resolve('blocked');
        });
        if (outcome === 'deleted') {
          deletedCount++;
        } else if (outcome === 'error') {
          errored.push(name);
        } else {
          blocked.push(name);
        }
      }

      if (staleNames.length > 0) {
        this.logger.info(
          `cleanupStaleDatabases: same-user DBs found=${staleNames.length} deleted=${deletedCount} blocked=${blocked.length} errored=${errored.length} (CVX-5918)`
        );
        if (blocked.length > 0) {
          this.logger.warn(`stale IDB deletion blocked (another tab has it open): ${blocked.join(', ')}`);
        }
        if (errored.length > 0) {
          this.logger.warn(`stale IDB deletion failed (onerror): ${errored.join(', ')}`);
        }
      }
    } catch (e) {
      this.logger.warn(`stale database cleanup failed: ${e}`);
    }
  }

  public table(tableName: string): Table {
    if (!this.db) {
      throw new Error(`LocalDB not initialized — cannot access table "${tableName}"`);
    }
    return this.db.table(tableName);
  }

}
